import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { RadioProgram, ScheduleView } from '@/mocks/adminSchedule';
import { adminSchedule, getStationSchedule, getNowPlaying, dayNames } from '@/mocks/adminSchedule';
import DayView from './components/DayView';
import WeekView from './components/WeekView';
import MonthView from './components/MonthView';
import ListView from './components/ListView';
import ProgramFormModal from './components/ProgramFormModal';

type PageState = 'loading' | 'error' | 'empty' | 'ready';

const views: { id: ScheduleView; label: string; icon: string }[] = [
  { id: 'dia',     label: 'Día',    icon: 'ri-sun-line' },
  { id: 'semana',  label: 'Semana', icon: 'ri-calendar-2-line' },
  { id: 'mes',     label: 'Mes',    icon: 'ri-calendar-check-line' },
  { id: 'lista',   label: 'Lista',  icon: 'ri-list-check-2' },
];

export default function AdminSchedulePage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [programs, setPrograms] = useState<RadioProgram[]>([]);
  const [activeView, setActiveView] = useState<ScheduleView>('dia');
  const [selectedDay, setSelectedDay] = useState(new Date().getDay());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<RadioProgram | null>(null);
  const [duplicateSource, setDuplicateSource] = useState<RadioProgram | null>(null);

  const loadPrograms = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.08;
      const shouldBeEmpty = Math.random() < 0.05;

      if (shouldError) {
        setPageState('error');
        setPrograms([]);
      } else if (shouldBeEmpty) {
        setPrograms([]);
        setPageState('empty');
      } else {
        setPrograms(getStationSchedule(1));
        setPageState('ready');
      }
    }, 800);
  }, []);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const { now, next } = pageState === 'ready' ? getNowPlaying(1) : { now: null, next: null };

  /* ─── Program mutations ─── */
  const handleSave = async (data: Partial<RadioProgram>) => {
    if (data.id && programs.find((p) => p.id === data.id)) {
      setPrograms((prev) => prev.map((p) => (p.id === data.id ? { ...p, ...data } as RadioProgram : p)));
      await SwalAlert.toast('Programa actualizado.', 'success');
    } else {
      const newId = Math.max(...programs.map((p) => p.id), 0) + 1;
      setPrograms((prev) => [...prev, { ...data, id: data.id || newId, isActive: true } as RadioProgram]);
      setPageState('ready');
      await SwalAlert.toast('Programa creado.', 'success');
    }
  };

  const handleDelete = async (program: RadioProgram) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar programa?',
      `Esta acción no se puede deshacer. Se eliminará permanentemente "${program.name}".`,
    );
    if (result.isConfirmed) {
      setPrograms((prev) => {
        const next = prev.filter((p) => p.id !== program.id);
        if (next.length === 0) setPageState('empty');
        return next;
      });
      await SwalAlert.toast('Programa eliminado.', 'info');
    }
  };

  const handleDuplicate = async (program: RadioProgram) => {
    const result = await SwalAlert.confirm(
      '¿Duplicar programa?',
      `Se creará una copia de "${program.name}" con "(copia)" en el nombre.`,
    );
    if (result.isConfirmed) {
      const newId = Math.max(...programs.map((p) => p.id), 0) + 1;
      const copy: RadioProgram = {
        ...program,
        id: newId,
        name: program.name + ' (copia)',
        isActive: true,
      };
      setPrograms((prev) => [...prev, copy]);
      setPageState('ready');
      await SwalAlert.toast('Programa duplicado.', 'success');
    }
  };

  const handleToggleActive = async (program: RadioProgram) => {
    const willBeActive = !program.isActive;
    setPrograms((prev) =>
      prev.map((p) => (p.id === program.id ? { ...p, isActive: willBeActive } : p)),
    );
    await SwalAlert.toast(
      willBeActive ? `"${program.name}" activado.` : `"${program.name}" desactivado.`,
      willBeActive ? 'success' : 'info',
    );
  };

  const handleCancel = async (program: RadioProgram) => {
    const result = await SwalAlert.confirm(
      '¿Cancelar programa?',
      `"${program.name}" será marcado como cancelado. No se emitirá pero se mantendrá en la grilla.`,
      'Sí, cancelar',
      'No',
    );
    if (result.isConfirmed) {
      setPrograms((prev) =>
        prev.map((p) => (p.id === program.id ? { ...p, cancelled: true, isActive: false } : p)),
      );
      await SwalAlert.toast(`"${program.name}" cancelado.`, 'info');
    }
  };

  const handleQuickTimeChange = async (program: RadioProgram) => {
    const { value: newStart } = await Swal.fire({
      title: 'Cambiar horario',
      html: `
        <div style="display:flex;flex-direction:column;gap:12px;text-align:left;">
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Hora inicio</label>
            <input id="swal-start" type="time" value="${program.startTime}" class="swal2-input" style="width:100%;padding:8px;border-radius:8px;border:1px solid #ddd;">
          </div>
          <div>
            <label style="font-size:12px;font-weight:600;display:block;margin-bottom:4px;">Hora fin</label>
            <input id="swal-end" type="time" value="${program.endTime}" class="swal2-input" style="width:100%;padding:8px;border-radius:8px;border:1px solid #ddd;">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      },
      buttonsStyling: false,
      preConfirm: () => {
        const start = (document.getElementById('swal-start') as HTMLInputElement)?.value;
        const end = (document.getElementById('swal-end') as HTMLInputElement)?.value;
        if (!start) {
          Swal.showValidationMessage('La hora de inicio es requerida');
          return false;
        }
        if (!end) {
          Swal.showValidationMessage('La hora de fin es requerida');
          return false;
        }
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        if (eh * 60 + em <= sh * 60 + sm) {
          Swal.showValidationMessage('La hora de fin debe ser mayor a la de inicio');
          return false;
        }
        return { startTime: start, endTime: end };
      },
    });

    if (newStart) {
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === program.id ? { ...p, startTime: newStart.startTime, endTime: newStart.endTime } : p,
        ),
      );
      await SwalAlert.toast('Horario actualizado.', 'success');
    }
  };

  const openEdit = (program: RadioProgram) => {
    setEditingProgram(program);
    setDuplicateSource(null);
    setModalOpen(true);
  };

  const openNew = () => {
    setEditingProgram(null);
    setDuplicateSource(null);
    setModalOpen(true);
  };

  const openDuplicate = (program: RadioProgram) => {
    setEditingProgram(null);
    setDuplicateSource(program);
    setModalOpen(true);
  };

  /* ─── Derived stats ─── */
  const totalPrograms = programs.filter((p) => p.isRecurring).length;
  const totalSpecials = programs.filter((p) => !p.isRecurring).length;
  const liveNow = programs.filter((p) => p.isLive && p.isActive).length;

  /* ════════════════════════════════════════════
     LOADING STATE
     ════════════════════════════════════════════ */
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-52 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
              {/* Stats skeletons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-3 animate-pulse">
                    <div className="w-8 h-8 rounded-lg bg-background-200 mb-2"></div>
                    <div className="h-6 w-10 rounded-md bg-background-200 mb-1"></div>
                    <div className="h-3 w-20 rounded-md bg-background-200"></div>
                  </div>
                ))}
              </div>

              {/* View switcher skeleton */}
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-20 h-9 rounded-lg bg-background-200 animate-pulse"></div>
                ))}
              </div>

              {/* Content skeleton */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-3 animate-pulse">
                    <div className="w-1 h-10 rounded-full bg-background-200"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-40 rounded-md bg-background-200"></div>
                      <div className="h-2.5 w-24 rounded-md bg-background-200"></div>
                    </div>
                    <div className="h-5 w-16 rounded-md bg-background-200"></div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     ERROR STATE
     ════════════════════════════════════════════ */
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-error-warning-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">Error al cargar programación</h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se pudo cargar la programación. Verificá tu conexión e intentá de nuevo.
              </p>
              <button
                onClick={loadPrograms}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     EMPTY STATE
     ════════════════════════════════════════════ */
  if (pageState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-calendar-2-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Programación</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            {/* Desktop toolbar */}
            <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Programación</h2>
                <p className="text-foreground-400 text-xs">Sin programas en la grilla</p>
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8 pb-8 flex items-center justify-center min-h-[60vh]">
              <div className="text-center max-w-sm">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-calendar-event-line text-foreground-400 text-2xl"></i>
                </div>
                <h2 className="text-lg font-bold text-foreground-950 mb-1">Sin programación</h2>
                <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                  No hay programas en la grilla todavía. Creá tu primer programa para empezar a organizar la radio.
                </p>
                <button
                  onClick={openNew}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line text-base"></i>
                  Crear programa
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     READY STATE
     ════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-calendar-2-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Programación</h1>
              <p className="text-foreground-400 text-[9px]">{totalPrograms} programas</p>
            </div>
          </div>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>


          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Programación</h2>
              <p className="text-foreground-400 text-xs">
                Organizá programas, locutores, eventos y lives. {totalPrograms} programas en total.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <button
                onClick={openNew}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-base"></i>
                Nuevo programa
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-broadcast-line text-primary-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{totalPrograms}</p>
                <p className="text-foreground-400 text-[11px]">Programas totales</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{liveNow}</p>
                <p className="text-foreground-400 text-[11px]">En vivo ahora</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i className="ri-star-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{totalSpecials}</p>
                <p className="text-foreground-400 text-[11px]">Eventos especiales</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center">
                    <i className="ri-time-line text-accent-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{now ? 'Al aire' : 'Off air'}</p>
                <p className="text-foreground-400 text-[11px] truncate">{now ? now.name : 'Sin emisión'}</p>
              </div>
            </div>

            {/* Now playing strip */}
            <div className="p-4 rounded-xl bg-background-50 border border-background-200 flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                {now ? (
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                ) : (
                  <span className="w-2.5 h-2.5 rounded-full bg-foreground-300"></span>
                )}
                <span className="text-foreground-700 text-xs font-semibold">
                  {now ? 'AHORA EN VIVO' : 'SIN EMISIÓN'}
                </span>
              </div>
              {now && (
                <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                  <span className="text-foreground-950 font-bold text-sm">{now.name}</span>
                  <span className="text-foreground-400 text-xs">· {now.startTime} - {now.endTime}</span>
                  <span className="text-foreground-400 text-xs">· {now.host}</span>
                </div>
              )}
              {next && (
                <div className="flex items-center gap-2 ml-auto shrink-0">
                  <span className="text-foreground-400 text-[11px] font-medium">Sig:</span>
                  <span className="text-foreground-700 text-[11px] font-semibold">{next.name}</span>
                  <span className="text-foreground-400 text-[10px]">{next.startTime}</span>
                </div>
              )}
            </div>

            {/* View switcher */}
            <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1 w-fit">
              {views.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setActiveView(v.id)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                    activeView === v.id
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${v.icon} text-sm`}></i>
                  {v.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <p className="text-xs text-foreground-400">
                {totalPrograms + totalSpecials} programas en total
              </p>
            </div>

            {/* Views */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
              {activeView === 'dia' && (
                <DayView
                  programs={programs}
                  selectedDay={selectedDay}
                  onChangeDay={setSelectedDay}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onQuickTimeChange={handleQuickTimeChange}
                  onToggleActive={handleToggleActive}
                  onCancel={handleCancel}
                />
              )}

              {activeView === 'semana' && (
                <WeekView
                  programs={programs}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onQuickTimeChange={handleQuickTimeChange}
                  onToggleActive={handleToggleActive}
                  onCancel={handleCancel}
                />
              )}

              {activeView === 'mes' && (
                <MonthView
                  programs={programs}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onQuickTimeChange={handleQuickTimeChange}
                  onToggleActive={handleToggleActive}
                  onCancel={handleCancel}
                />
              )}

              {activeView === 'lista' && (
                <ListView
                  programs={programs}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onQuickTimeChange={handleQuickTimeChange}
                  onToggleActive={handleToggleActive}
                  onCancel={handleCancel}
                />
              )}
            </div>
          </div>




      {/* Program Form Modal */}
      <ProgramFormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingProgram(null); setDuplicateSource(null); }}
        onSave={handleSave}
        program={editingProgram}
        prefillName={duplicateSource ? duplicateSource.name + ' (copia)' : undefined}
      />
    </div>
  );
}